"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { ReactLenis, useLenis } from "lenis/react";
import { gsap, ScrollTrigger, registerGsap } from "@/lib/gsap-setup";
import { isBlogPath } from "@/lib/blog/is-blog-path";

function GsapBridge() {
  const lenis = useLenis();
  useEffect(() => {
    if (!lenis) return;
    registerGsap();
    const update = () => ScrollTrigger.update();
    lenis.on("scroll", update);
    const tickerCb = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(tickerCb);
    gsap.ticker.lagSmoothing(0);

    // ScrollTrigger measures every trigger's start/end up-front from document
    // flow, so any post-init layout shift — the display:swap font landing, a
    // next/dynamic chunk mounting, a content-visibility section first painting —
    // leaves triggers firing at stale scroll positions. Recompute once the page
    // has settled: after fonts load, and again after lazy chunks resolve.
    let raf = 0;
    const refresh = () => ScrollTrigger.refresh();
    document.fonts?.ready.then(() => {
      raf = requestAnimationFrame(refresh);
    });
    const settle = window.setTimeout(refresh, 1200);

    return () => {
      lenis.off("scroll", update);
      gsap.ticker.remove(tickerCb);
      cancelAnimationFrame(raf);
      window.clearTimeout(settle);
    };
  }, [lenis]);
  return null;
}

type Props = { children: React.ReactNode };

export function LenisProvider({ children }: Props) {
  const pathname = usePathname();
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    // Lenis on desktop / tablet only — let native scroll run on mobile
    // for performance on mid-tier Android.
    const mq = window.matchMedia("(min-width: 768px)");
    // eslint-disable-next-line react-hooks/set-state-in-effect -- mount-time matchMedia init (SSR-safe; no window on server)
    setEnabled(mq.matches);
    const onChange = () => setEnabled(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Founder ban (blog spec, decision #2): no scroll-jacking on blog surfaces.
  // Reading routes get native scrolling — no Lenis, no ScrollTrigger bridge.
  if (!enabled || isBlogPath(pathname)) return <>{children}</>;

  return (
    // autoRaf:false — GsapBridge already drives lenis.raf from the gsap ticker.
    // Without this, ReactLenis runs its OWN rAF too, so lenis.raf is called
    // twice per frame and ScrollTrigger sees a doubled/flipped scroll delta
    // (scrub animations stick, direction inverts). The gsap ticker is the sole
    // driver so Lenis and ScrollTrigger stay on one clock.
    <ReactLenis root options={{ duration: 1.1, smoothWheel: true, autoRaf: false }}>
      <GsapBridge />
      {children}
    </ReactLenis>
  );
}
